import type { CSSProperties } from 'react';

import type { ColorName, ColorVariant, Style } from './colors';
import type { Shape, Size } from './types';
import { cn } from '../cn';
import { colorRegistry, composeVariant, parseVariant } from './colors';
import { shapeRounded, sizeFont, sizeGap, sizeHeight, sizePaddingL, sizePaddingR, sizeWidth } from './types';

export type { ColorName, ColorVariant };
export { colorRegistry, composeVariant, parseVariant };

// Local aliases for readability.
type Active = boolean;
type Disabled = boolean;
type Bordered = boolean;

// Underline look (tone="underline"): keep the colored all-sides border, make the
// 3 non-bottom sides transparent (preserves the 1px box model, so no layout shift),
// and drop rounding. The `dark:` overrides are required because Tailwind's dark
// border-color rule (`.dark .dark:border-…`, specificity 0,2,0) outranks the
// unscoped `border-t-transparent` (0,1,0) — so in dark mode the sides need a
// dark-scoped transparent at matching specificity to win.
const BOTTOM_BORDER_ONLY =
  'border-t-transparent border-x-transparent dark:border-t-transparent dark:border-x-transparent rounded-none';

// The `underline` tone has no dedicated registry entry — it reuses the color's
// `outline` set for border/text; the two core builders (inputVariantClasses,
// variantClasses) strip its fill + focus ring and force the bottom-only border.
function baseStyle(style: Style): 'filled' | 'outline' | 'soft' {
  return style === 'underline' ? 'outline' : style;
}

// ---------------------------------------------------------------------------
// textVariantClasses — text={true} mode
// ---------------------------------------------------------------------------

export function textVariantClasses(variant: ColorVariant, active: Active, disabled: Disabled) {
  const p = parseVariant(variant);
  const c = colorRegistry[p.color];

  if (p.style === 'soft') {
    const s = c.textSoft;
    return cn(
      !active && s.text,
      active && s.activeBg,
      !disabled && !active && s.hover,
      !disabled && !active && s.hoverDark,
    );
  }
  // outline + filled both fall back to the color's filled text-mode styling.
  const f = c.textFilled;
  return cn(
    !active && f.text,
    active && f.activeBg,
    !disabled && !active && f.hover,
    !disabled && !active && f.hoverDark,
  );
}

// ---------------------------------------------------------------------------
// variantClasses — filled mode (text={false})
// ---------------------------------------------------------------------------

export function variantClasses(variant: ColorVariant, active: Active, disabled: Disabled, bordered: Bordered) {
  const p = parseVariant(variant);
  const c = colorRegistry[p.color];

  if (p.style === 'underline') {
    // Underline tone: bottom border (outline border color) + text-button colors
    // on a transparent fill. buttonClassName layers on BOTTOM_BORDER_ONLY.
    const o = c.outline;
    const t = c.textFilled;
    return cn(
      bordered && o.border,
      !active && t.text,
      active && t.activeBg,
      !disabled && !active && t.hover,
      !disabled && !active && t.hoverDark,
    );
  }
  if (p.style === 'filled') {
    const f = c.filled;
    return cn(
      bordered && f.border,
      !active && f.bg,
      active && f.bgActive,
      !disabled && f.hover,
      !disabled && f.hoverDark,
    );
  }
  if (p.style === 'outline') {
    const o = c.outline;
    return cn(
      bordered && o.border,
      !active && o.bg,
      active && o.bgActive,
      !disabled && !active && o.hover,
      !disabled && !active && o.hoverDark,
      !disabled && active && o.hoverWhenActive,
    );
  }
  // soft
  const s = c.soft;
  return cn(
    bordered && s.border,
    !active && s.bg,
    active && s.bgActive,
    !disabled && !active && s.hover,
    !disabled && !active && s.hoverDark,
    !disabled && active && s.hoverWhenActive,
  );
}

// ---------------------------------------------------------------------------
// buttonClassName (top-level builder, called by Button.tsx)
// ---------------------------------------------------------------------------

export type ButtonClassNameParams = {
  size?: Size;
  bordered?: Bordered;
  shape?: Shape;
  icon?: boolean;
  rIcon?: boolean;
  children?: boolean;
  error?: boolean;
  variant?: ColorVariant;
  active?: boolean;
  text?: boolean;
  filled?: boolean;
  /** Use the input/field color system instead of the button palette. */
  inputStyle?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  tooltip?: boolean;
  className?: string;
  noPadding?: boolean;
};

export function buttonClassName({
  size = 'md',
  bordered = true,
  shape,
  icon,
  rIcon,
  children,
  error,
  variant = 'gray',
  active = false,
  text,
  filled,
  inputStyle,
  disabled,
  readOnly,
  tooltip,
  className,
  noPadding,
}: ButtonClassNameParams) {
  const noInteraction = !!disabled || !!readOnly;
  const effectiveBordered = text ? false : bordered;
  // Underline tone: bottom-border-only, transparent fill (handled in variantClasses).
  const isUnderline = !inputStyle && parseVariant(variant).style === 'underline';

  // For noPadding (icon-only), force the button to be a square — width matches height.
  const noPaddingWidthClass = noPadding ? sizeWidth(size) : undefined;

  return cn(
    'inline-flex items-center justify-center shrink-0 font-sans font-medium transition-colors cursor-pointer',
    // Always a named group: the tooltip and the soft-tone icon both key off it.
    'group/btn',
    sizeHeight(size),
    noPaddingWidthClass,
    shapeRounded(shape, size),
    sizeFont(size),
    noPadding ? '' : sizeGap(size),
    !icon && !noPadding && sizePaddingL(size),
    !rIcon && !!children && !noPadding && sizePaddingR(size),
    'border',
    // inputStyle: defer all color/border/disabled handling to the input system
    // so the trigger matches Input/Select exactly.
    inputStyle && inputVariantClasses(variant, effectiveBordered, !!error, !!disabled, !!readOnly),
    !inputStyle && !effectiveBordered && !error && 'border-transparent',
    !inputStyle && error && 'border-fair-pink-300 dark:border-fair-pink-900',
    !inputStyle &&
      !error &&
      (text
        ? textVariantClasses(variant, active, noInteraction)
        : variantClasses(variant, active, noInteraction, effectiveBordered)),
    !inputStyle && !error && filled && variant === 'gray' && 'bg-white dark:bg-slate-800',
    // Underline error keeps the pink text/border but drops the pink fill.
    !inputStyle && error && !isUnderline && 'bg-fair-pink-100 dark:bg-fair-pink-950',
    !inputStyle && error && 'text-fair-pink-600 dark:text-fair-pink-400',
    !inputStyle && disabled && !tooltip && 'cursor-not-allowed opacity-50',
    !inputStyle && disabled && tooltip && 'cursor-not-allowed [&>:not([role=tooltip])]:opacity-50',
    readOnly && 'cursor-default',
    // tone="underline": bottom-border-only. Applies to both the inputStyle and
    // palette paths (idempotent — cn dedupes).
    isUnderline && BOTTOM_BORDER_ONLY,
    className,
  );
}

// ---------------------------------------------------------------------------
// Tooltip positioning helper (unchanged)
// ---------------------------------------------------------------------------

export type TooltipPosition =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top span-right'
  | 'top span-left'
  | 'bottom span-right'
  | 'bottom span-left';

export const tooltipStyle = (
  anchorName: string | undefined,
  position: TooltipPosition = 'top',
  fallbacksOverride?: string,
): CSSProperties => {
  const mainPosition = position.split(' ')[0] as 'top' | 'right' | 'bottom' | 'left';
  const hasSpan = position.includes('span');

  const fallbacks: Record<string, string> = {
    top: 'flip-block',
    bottom: 'flip-block',
    right: 'flip-inline, flip-block',
    left: 'flip-inline, flip-block',
  };

  return {
    position: 'fixed',
    positionAnchor: anchorName,
    positionArea: position,
    positionTryFallbacks: fallbacksOverride ?? fallbacks[mainPosition],
    margin: 0,
    inset: 'auto',
    marginBottom: mainPosition === 'top' ? '6px' : undefined,
    marginTop: mainPosition === 'bottom' ? '6px' : undefined,
    marginRight: mainPosition === 'left' ? '6px' : undefined,
    marginLeft: mainPosition === 'right' ? '6px' : undefined,
    marginInline: !hasSpan && (mainPosition === 'top' || mainPosition === 'bottom') ? '0.5rem' : undefined,
    zIndex: 50,
  } as CSSProperties;
};

// ---------------------------------------------------------------------------
// tooltipClassName / badgeClassName / softIconColorClasses / underline / inputs
// ---------------------------------------------------------------------------

export function tooltipClassName(variant: ColorVariant, allowWrap = false) {
  const p = parseVariant(variant);
  const colorClass = colorRegistry[p.color].tooltip;
  return cn(
    'pointer-events-none',
    'max-w-[calc(100vw-1rem)]',
    allowWrap ? 'whitespace-pre-line' : 'whitespace-nowrap',
    'px-2 py-1 rounded-md text-xs font-normal shadow-lg text-left',
    colorClass,
  );
}

const BADGE_BASE =
  'absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold pointer-events-none';

export function badgeClassName(variant: ColorVariant) {
  const p = parseVariant(variant);
  const b = colorRegistry[p.color].badge;
  return cn(BADGE_BASE, p.style === 'soft' ? b.soft : b.filled);
}

/** Returns icon color classes for soft variants (which have tinted text but need colored icons). */
export function softIconColorClasses(variant: ColorVariant, active = false) {
  const p = parseVariant(variant);
  // In the active state a soft button's fill flips to the dark accent bg with
  // white text (see `soft.bgActive`). The icon must follow — drop the resting
  // tint so it inherits the button's active text color via currentColor, rather
  // than render its dark resting tint on the dark active fill (dark-on-dark).
  if (active) return '';
  if (p.style === 'soft') return colorRegistry[p.color].softIcon;
  return '';
}

/** Returns classes to style <u> tags inside buttons with variant-appropriate underline decoration colors. */
export function underlineDecorationClasses(variant: ColorVariant) {
  const p = parseVariant(variant);
  return cn(
    '[&_u]:font-medium [&_u]:underline-offset-2',
    colorRegistry[p.color].underline[baseStyle(p.style)],
    // Soft buttons hover into their accent fill with white text; the underlined word must
    // follow the button's colour there instead of keeping its dark resting tint.
    p.style === 'soft' && 'group-hover/btn:[&_u]:text-current group-hover/btn:[&_u]:decoration-current',
  );
}

// ---------------------------------------------------------------------------
// inputVariantClasses / inputVariantMutedText / inputVariantPlaceholder
// ---------------------------------------------------------------------------

export function inputVariantClasses(
  variant: ColorVariant = 'gray',
  bordered: Bordered,
  error: boolean,
  disabled: boolean,
  readOnly: boolean,
) {
  // `underline` tone: bottom border only, transparent fill, no focus ring.
  const isUnderline = parseVariant(variant).style === 'underline';

  if (error) {
    return cn(
      bordered && 'border-fair-pink-300 dark:border-fair-pink-900',
      !bordered && 'border-transparent',
      isUnderline && BOTTOM_BORDER_ONLY,
      !isUnderline && 'bg-fair-pink-100 dark:bg-fair-pink-950',
      'text-fair-pink-600 dark:text-fair-pink-400',
    );
  }
  if (disabled) {
    return cn(
      bordered && 'border-slate-200 dark:border-charade-700',
      !bordered && 'border-transparent',
      isUnderline && BOTTOM_BORDER_ONLY,
      !isUnderline && 'bg-stone-50 dark:bg-slate-950',
      'cursor-not-allowed',
      'text-stone-500 dark:text-slate-500',
    );
  }

  const p = parseVariant(variant);
  const colors = colorRegistry[p.color].input[baseStyle(p.style)];

  return cn(
    bordered && colors.border,
    !bordered && 'border-transparent',
    isUnderline && BOTTOM_BORDER_ONLY,
    // Transparent base outline color so the focus ring (which transitions via
    // transition-colors) fades in from transparent rather than from the element's
    // dark currentColor — otherwise light variants flash a dark ring on focus.
    !isUnderline && !readOnly && bordered && 'outline-transparent',
    // Underline tone drops all fill (resting/focus/hover) and the focus ring.
    !isUnderline && !readOnly && colors.bg,
    !isUnderline && !readOnly && colors.focusBg,
    !isUnderline && !readOnly && bordered && colors.focusOutline,
    !isUnderline && !readOnly && colors.hoverActive,
    !isUnderline && readOnly && colors.readOnlyBg,
    colors.text,
  );
}

/** Returns the muted text color for a variant (used for placeholder text and empty-state labels). */
export function inputVariantMutedText(variant: ColorVariant = 'gray', error: boolean) {
  if (error) return 'text-fair-pink-400 dark:text-fair-pink-700';
  const p = parseVariant(variant);
  return colorRegistry[p.color].inputMuted[baseStyle(p.style)];
}

export function inputVariantPlaceholder(variant: ColorVariant = 'gray', error: boolean) {
  if (error) return 'placeholder:text-fair-pink-400 dark:placeholder:text-fair-pink-700';
  const p = parseVariant(variant);
  return colorRegistry[p.color].inputPlaceholder[baseStyle(p.style)];
}

// ---------------------------------------------------------------------------
// ButtonGroup / ButtonSegment / FilterPopover helpers — exposed so the
// downstream components stay terse.
// ---------------------------------------------------------------------------

export function groupVariantClasses(variant: ColorVariant) {
  const p = parseVariant(variant);
  return colorRegistry[p.color].group[baseStyle(p.style)];
}

export function groupDividerVariantClasses(variant: ColorVariant) {
  const p = parseVariant(variant);
  return colorRegistry[p.color].groupDivider[baseStyle(p.style)];
}

export function segmentVariantClasses(variant: ColorVariant) {
  const p = parseVariant(variant);
  // Soft is not supported by ButtonSegment today — fall back to gray/filled.
  if (p.style === 'soft') return colorRegistry.gray.segment.filled;
  const s = p.style === 'underline' ? 'outline' : p.style;
  return colorRegistry[p.color].segment[s];
}

export function segmentDividerVariantClasses(variant: ColorVariant) {
  const p = parseVariant(variant);
  if (p.style === 'soft') return colorRegistry.gray.segmentDivider.filled;
  const s = p.style === 'underline' ? 'outline' : p.style;
  return colorRegistry[p.color].segmentDivider[s];
}

export function popoverHighlightClasses(variant: ColorVariant) {
  const p = parseVariant(variant);
  // Soft variants historically fall through to the gray (formerly "default") highlight.
  if (p.style === 'soft') return colorRegistry.gray.popoverHighlight;
  return colorRegistry[p.color].popoverHighlight;
}

export function popoverTextClasses(variant: ColorVariant) {
  const p = parseVariant(variant);
  if (p.style === 'soft') return colorRegistry.gray.popoverText;
  return colorRegistry[p.color].popoverText;
}

export function popoverBorderClasses(variant: ColorVariant) {
  const p = parseVariant(variant);
  if (p.style === 'soft') return colorRegistry.gray.popoverBorder;
  return colorRegistry[p.color].popoverBorder;
}

export { sizeIconPaddingL, sizeIconPaddingR, sizeIconClass } from './types';

// Re-export Style for downstream consumers that want to branch on it.
export type { Style };
